import type { Node, XYPosition } from "@xyflow/react";
import { z } from "zod";
import type { BoardNodeData, StickyNoteColor } from "../types/board";
import { getNodeCapabilities, isStickyColor, type NodeFormattingDefaults } from "./node-formatting";

export type BoardNodeType = BoardNodeData["type"];

export const NODE_DEFAULT_SIZES: Record<BoardNodeType, { width: number; height: number }> = {
  rectangle: { width: 160, height: 80 },
  circle: { width: 120, height: 120 },
  diamond: { width: 140, height: 140 },
  hexagon: { width: 160, height: 104 },
  stickyNote: { width: 180, height: 120 },
  text: { width: 160, height: 40 },
  orgChartCard: { width: 240, height: 100 },
};

const initialData: { [Type in BoardNodeType]: Extract<BoardNodeData, { type: Type }> } = {
  rectangle: { type: "rectangle", label: "", bgColor: "", borderColor: "" },
  circle: { type: "circle", label: "", bgColor: "", borderColor: "" },
  diamond: { type: "diamond", label: "", bgColor: "", borderColor: "" },
  hexagon: { type: "hexagon", label: "", bgColor: "", borderColor: "" },
  stickyNote: { type: "stickyNote", text: "", color: "yellow" },
  text: { type: "text", text: "Text", fontSize: 14 },
  orgChartCard: { type: "orgChartCard", name: "Name", title: "Title", department: "Department", bgColor: "#3b82f6" },
};

const fontSizeSchema = z.number().positive();
const geometricDropSchema = z.looseObject({
  label: z.string(), bgColor: z.string(), borderColor: z.string(), fontSize: fontSizeSchema.optional(),
});
const nodeDropSchema = z.discriminatedUnion("type", [
  geometricDropSchema.extend({ type: z.literal("rectangle") }),
  geometricDropSchema.extend({ type: z.literal("circle") }),
  geometricDropSchema.extend({ type: z.literal("diamond") }),
  geometricDropSchema.extend({ type: z.literal("hexagon") }),
  z.looseObject({
    type: z.literal("stickyNote"), text: z.string(), fontSize: fontSizeSchema.optional(),
    color: z.custom<StickyNoteColor>((value) => typeof value === "string" && isStickyColor(value)),
  }),
  z.looseObject({ type: z.literal("text"), text: z.string(), fontSize: fontSizeSchema }),
  z.looseObject({
    type: z.literal("orgChartCard"), name: z.string(), title: z.string(), department: z.string(),
    avatarUrl: z.string().optional(), bgColor: z.string(),
  }),
]);

export function createNodeData(
  type: BoardNodeType,
  defaults: NodeFormattingDefaults = { bgColor: "", fontSize: 14 },
): BoardNodeData {
  const support = getNodeCapabilities(type)!;
  return {
    ...initialData[type],
    ...(support.fill === "bgColor" && defaults.bgColor ? { bgColor: defaults.bgColor } : {}),
    ...(support.fontSize && defaults.fontSize !== 14 ? { fontSize: defaults.fontSize } : {}),
  };
}

export function createBoardNode(id: string, data: BoardNodeData, position: XYPosition): Node {
  return {
    id, type: data.type, position: { ...position }, data: { ...data }, selected: true,
    style: { ...NODE_DEFAULT_SIZES[data.type] },
  };
}

/** Read the existing toolbar drag contract; invalid external drops are a no-op. */
export function parseNodeDrop(type: string, serialized: string): BoardNodeData | null {
  if (!Object.prototype.hasOwnProperty.call(initialData, type)) return null;
  try {
    const data: unknown = JSON.parse(serialized);
    if (!data || typeof data !== "object" || Array.isArray(data) || !("type" in data) || data.type !== type) return null;
    const result = nodeDropSchema.safeParse({ ...initialData[type as BoardNodeType], ...data });
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}
